import React, { memo, useMemo } from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DiaryEntrySyncStatus } from 'graphql/types';
import { useFeatureFlag } from 'modules/common/hooks';
import { useAutoPilotContext } from 'modules/App/menu/items/Settings/AutoPilotContextProvider';
import { SmartDiaryEntry } from '../type';
import { LabelsTooltip } from './LabelsTooltip';
import { SmartListItemIcon } from './SmartListItemIcon';
import { SmartListItemText } from './SmartListItemText';
import { SmartListItemAction } from './SmartListItemAction';

type SmartListItemProps = { smartEntry: SmartDiaryEntry };
type ListStyleProps = {
  isSynced: boolean;
  isTiwVpatEnabled: boolean;
};

type UseStyleProps = {
  isAutoPilotEnabled: boolean;
  isTiwVpatEnabled: boolean;
};

const useListStyle = makeStyles((theme) => ({
  root: ({ isTiwVpatEnabled }: ListStyleProps) => {
    if (isTiwVpatEnabled) {
      return {
        display: 'flex',
        width: '100%',
        '&:hover, &:focus': {
          backgroundColor: theme.palette.common.white,
          boxShadow: theme.shadows[2]
        },
        '&:focus': {
          outline: theme.spacing(0)
        },
        marginBottom: theme.spacing(0.25)
      };
    }
    return {
      display: 'flex',
      width: `calc(100% - ${theme.spacing(3)})`,
      '&:focus': {
        outline: theme.spacing(0)
      }
    };
  },
  gutters: {
    padding: ({ isTiwVpatEnabled }: ListStyleProps) =>
      isTiwVpatEnabled ? theme.spacing(0) : theme.spacing(0.125, 1)
  },
  container: {
    background: ({ isSynced }: ListStyleProps) =>
      isSynced ? '#fafafa' : theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    paddingLeft: ({ isTiwVpatEnabled }: ListStyleProps) =>
      isTiwVpatEnabled ? theme.spacing(0) : theme.spacing(1),
    '&:last-child': {
      borderBottom: '1px solid transparent'
    },
    '&:only-child': {
      marginBottom: theme.spacing(0),
      marginTop: theme.spacing(0),
      borderBottom: '1px solid transparent'
    }
  }
}));

const useStyles = makeStyles((theme) => ({
  listIcon: {
    minWidth: ({ isAutoPilotEnabled }: UseStyleProps) =>
      isAutoPilotEnabled ? theme.spacing(1) : theme.spacing(3),
    marginLeft: ({ isTiwVpatEnabled }: UseStyleProps) =>
      isTiwVpatEnabled ? theme.spacing(2) : theme.spacing(0)
  },
  listAction: {
    display: 'flex',
    marginRight: ({ isTiwVpatEnabled }: { isTiwVpatEnabled: boolean }) =>
      isTiwVpatEnabled ? theme.spacing(-1) : theme.spacing(-2.75)
  },
  text: {
    display: 'flex',
    justifyContent: 'space-between'
  }
}));

export const SmartListItem: React.FC<SmartListItemProps> = memo(
  ({ smartEntry }: SmartListItemProps) => {
    const isSynced = useMemo(
      () => smartEntry.syncStatus === DiaryEntrySyncStatus.Synced,
      [smartEntry.syncStatus]
    );
    const isTiwVpatEnabled = useFeatureFlag('isTiwVpatEnabled');
    const { isAutoPilotEnabled } = useAutoPilotContext();

    const classes = useStyles({ isTiwVpatEnabled, isAutoPilotEnabled });
    return (
      <ListItem
        data-testid={`smart-entry-item_root_${smartEntry.id}`}
        classes={useListStyle({
          isSynced,
          isTiwVpatEnabled
        })}
        tabIndex={0}
      >
        <ListItemIcon className={classes.listIcon}>
          <SmartListItemIcon smartEntry={smartEntry} />
        </ListItemIcon>
        <LabelsTooltip smartEntry={smartEntry}>
          <ListItemText
            data-testid={`smart-entry-item_cpt_${smartEntry.id}`}
            primary={useMemo(
              () => (
                <SmartListItemText smartEntry={smartEntry} />
              ),
              [smartEntry]
            )}
          />
        </LabelsTooltip>
        <ListItemSecondaryAction className={classes.listAction}>
          <SmartListItemAction smartEntry={smartEntry} />
        </ListItemSecondaryAction>
      </ListItem>
    );
  }
);

SmartListItem.displayName = 'SmartListItem';
